import { yaml as yamlLang } from '@codemirror/lang-yaml';
import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import { VALUES_CONFIG_INFO } from '../infoDoc';
import { useGeneratorsStyles, useStyles } from '../styles';
import { HelmConfiguration } from '../types';
import {
  validateSourceGitURL,
  validateSourceInputPattern,
  validateSourceRevision,
} from '../validation';
import { RepoUrlPicker } from './RepoUrlPicker';

const MAX_VALUE_FILES = 20;

function deriveValuesObjectText(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

interface HelmValuesConfigProps {
  helm: HelmConfiguration;
  onChange: (value: HelmConfiguration) => void;
  enableAppSetSupport: boolean;
  errors: string[];
}

export const HelmValuesConfig = ({
  helm,
  onChange,
  enableAppSetSupport,
  errors,
}: HelmValuesConfigProps) => {
  const classes = useStyles();
  const generatorClasses = useGeneratorsStyles();

  const valuesSourceEnabled = Boolean(helm.valuesSource);
  const valuesObjectText = deriveValuesObjectText(helm.valuesObject);

  const handleValueFileAdd = () => {
    if (helm.valueFiles.length >= MAX_VALUE_FILES) return;
    onChange({ ...helm, valueFiles: [...helm.valueFiles, ''] });
  };

  const handleValueFileChange = (index: number, v: string) => {
    const next = [...helm.valueFiles];
    next[index] = v;
    onChange({ ...helm, valueFiles: next });
  };

  const handleValueFileRemove = (index: number) => {
    onChange({
      ...helm,
      valueFiles: helm.valueFiles.filter((_, i) => i !== index),
    });
  };

  const handleValuesSourceToggle = (enabled: boolean) => {
    if (enabled) {
      onChange({
        ...helm,
        valuesSource: helm.valuesSource ?? {
          repoURL: '',
          targetRevision: '',
        },
      });
    } else {
      const { valuesSource: _drop, ...rest } = helm;
      onChange(rest);
    }
  };

  const handleValuesSourceField = (
    field: 'repoURL' | 'targetRevision',
    v: string,
  ) => {
    const existing = helm.valuesSource ?? { repoURL: '', targetRevision: '' };
    onChange({ ...helm, valuesSource: { ...existing, [field]: v } });
  };

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Values Configuration"
          description="Define where Helm values come from and how they're applied."
          infoDoc={VALUES_CONFIG_INFO}
        />
      </Box>

      <Box className={classes.inputContainer}>
        <FormControlLabel
          control={
            <Switch
              color="secondary"
              checked={valuesSourceEnabled}
              onChange={e => handleValuesSourceToggle(e.target.checked)}
            />
          }
          label="Use a separate Git repository for value files"
        />
      </Box>

      {valuesSourceEnabled && (
        <Box>
          <Box className={classes.inputContainer}>
            <RepoUrlPicker
              value={helm.valuesSource?.repoURL ?? ''}
              onChange={url => handleValuesSourceField('repoURL', url)}
              label="Git URL"
              required
              freeSolo={false}
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceGitURL(helm.valuesSource?.repoURL ?? '')
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceGitURL(helm.valuesSource?.repoURL ?? '') ? (
                  <ScaffolderFieldError message="Git URL must be a TomTom Git repository (github.com/tomtom-internal/* or tomtom-international/*)." />
                ) : (
                  'URL of the TomTom Git repository containing the value files.'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label="Git Revision"
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={helm.valuesSource?.targetRevision ?? ''}
              onChange={e =>
                handleValuesSourceField('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(helm.valuesSource?.targetRevision ?? '')
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(
                  helm.valuesSource?.targetRevision ?? '',
                ) ? (
                  <ScaffolderFieldError message="Revision must be a valid Git branch, tag, or commit hash (letters, numbers, dots, hyphens, underscores, or slashes)." />
                ) : (
                  'The Git revision of the values source (branch, tag, or commit hash).'
                )
              }
            />
          </Box>
        </Box>
      )}

      {helm.valueFiles.map((vf, index) => (
        <Box key={index} className={classes.flexContainer}>
          <Box className={classes.inputContainer}>
            <TextField
              label="Value File"
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={vf}
              onChange={e => handleValueFileChange(index, e.target.value)}
              inputProps={{ 'aria-label': `Value File ${index + 1}` }}
              error={
                Boolean(errors?.length > 0) &&
                (!vf ||
                  !validateSourceInputPattern(vf, { enableAppSetSupport }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!vf ||
                  !validateSourceInputPattern(vf, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message="Value file path must contain only letters, numbers, dots, hyphens, underscores, asterisks, or slashes." />
                ) : undefined
              }
            />
          </Box>
          <Box className={classes.removeButtonContainer}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              aria-label={`Remove Value File ${index + 1}`}
              onClick={() => handleValueFileRemove(index)}
            >
              Remove
            </Button>
          </Box>
        </Box>
      ))}

      <Box className={classes.inputContainer}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleValueFileAdd}
          color="secondary"
          disabled={helm.valueFiles.length >= MAX_VALUE_FILES}
        >
          Add Value File
        </Button>
      </Box>

      <Box className={classes.inputContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={helm.ignoreMissingValueFiles}
              onChange={e =>
                onChange({
                  ...helm,
                  ignoreMissingValueFiles: e.target.checked,
                })
              }
            />
          }
          label="Ignore Missing Value Files"
        />
      </Box>

      {enableAppSetSupport && (
        <Box className={classes.inputContainer}>
          {helm.valuesObject !== undefined ? (
            <>
              <ScaffolderInputHeader
                title="Values Overrides (YAML)"
                description="For Helm values that reference ApplicationSet generator variables (e.g. image: '{{ .head_sha }}'). Static values belong in value files."
              />
              <Box className={generatorClasses.codeMirror}>
                <CodeMirror
                  value={valuesObjectText}
                  minHeight="160px"
                  extensions={[
                    typeof yamlLang === 'function' ? yamlLang() : ([] as any),
                  ]}
                  theme={githubLight}
                  onChange={v =>
                    onChange({
                      ...helm,
                      valuesObject: v as unknown as Record<string, unknown>,
                    })
                  }
                />
              </Box>
              <Box mt={1}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={() => onChange({ ...helm, valuesObject: undefined })}
                >
                  Remove Values Overrides
                </Button>
              </Box>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  ...helm,
                  valuesObject: '' as unknown as Record<string, unknown>,
                })
              }
              color="secondary"
            >
              Add Values Overrides
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
